import { useEffect, useMemo, useState, useCallback } from "react";
import {
    Eye, RefreshCw, AlertCircle, Loader2, TrendingUp, Repeat, ExternalLink, Filter,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { ChannelLink } from "@/components/ChannelLink";
import { listEvents, listGroups } from "@/lib/api";

const fmt = (n) => (n == null ? "—" : Number(n).toLocaleString("en-US"));

const sevMeta = {
    critical: { label: "حرج", cls: "border-rose-500/30 text-rose-300 bg-rose-500/10" },
    warning: { label: "تحذير", cls: "border-amber-500/30 text-amber-300 bg-amber-500/10" },
    info: { label: "معلومة", cls: "border-cyan-500/30 text-cyan-300 bg-cyan-500/10" },
    safe: { label: "آمن", cls: "border-emerald-500/30 text-emerald-300 bg-emerald-500/10" },
};

function Kpi({ label, value, icon: Icon, color = "cyan", testId }) {
    const ring = {
        cyan: "ring-cyan-500/20 text-cyan-400",
        rose: "ring-rose-500/20 text-rose-400",
        amber: "ring-amber-500/20 text-amber-400",
        violet: "ring-violet-500/20 text-violet-400",
    }[color];
    return (
        <Card className={`bg-card/50 ring-1 ${ring}`} data-testid={testId}>
            <CardContent className="p-4 flex items-center justify-between">
                <div>
                    <p className="text-xs text-muted-foreground">{label}</p>
                    <p className="text-2xl font-bold mt-1" dir="ltr">{fmt(value)}</p>
                </div>
                {Icon && <Icon className="h-5 w-5 opacity-50" />}
            </CardContent>
        </Card>
    );
}

function fmtDate(ts) {
    if (!ts) return "—";
    try {
        const d = new Date(ts);
        return d.toLocaleString("ar", { dateStyle: "short", timeStyle: "short" });
    } catch { return String(ts).slice(0, 16); }
}

export default function FakeViewsAlerts() {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [events, setEvents] = useState([]);
    const [groups, setGroups] = useState({});
    const [severity, setSeverity] = useState("all");

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [ev, gs] = await Promise.all([
                listEvents({ event_type: "fakeviews_spike_mtproto", limit: 300 }),
                listGroups(),
            ]);
            setEvents(ev.events || []);
            const list = Array.isArray(gs) ? gs : (gs?.groups || []);
            const map = {};
            list.forEach((g) => { map[g.chat_id] = { username: g.username, title: g.title }; });
            setGroups(map);
        } catch (e) {
            const status = e?.response?.status;
            if (status === 403) setError("هذه الصفحة مخصّصة لمالك المنصّة فقط.");
            else setError(e?.response?.data?.detail || "تعذّر تحميل تنبيهات المشاهدات.");
        }
        setLoading(false);
    }, []);

    useEffect(() => {
        load();
        const t = setInterval(load, 30000);
        return () => clearInterval(t);
    }, [load]);

    const filtered = useMemo(() => {
        if (severity === "all") return events;
        return events.filter((e) => e.severity === severity);
    }, [events, severity]);

    const stats = useMemo(() => {
        const channels = new Set();
        let critical = 0, repeats = 0;
        events.forEach((e) => {
            channels.add(e.chat_id);
            if (e.severity === "critical") critical += 1;
            if (e.details?.is_repeat) repeats += 1;
        });
        return { total: events.length, critical, channels: channels.size, repeats };
    }, [events]);

    if (loading) {
        return (
            <div className="space-y-4" data-testid="views-alerts-loading">
                <Skeleton className="h-20 w-full" />
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-24" />)}
                </div>
                <Skeleton className="h-96 w-full" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex items-center justify-center py-24" data-testid="views-alerts-error">
                <Card className="bg-card/60 border-border/40 max-w-md w-full">
                    <CardContent className="p-8 text-center space-y-3">
                        <AlertCircle className="h-10 w-10 mx-auto text-rose-400" />
                        <p className="text-sm text-muted-foreground">{error}</p>
                        <Button variant="outline" onClick={load} className="gap-2"><RefreshCw className="h-4 w-4" /> إعادة المحاولة</Button>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="space-y-5 pb-10" data-testid="views-alerts-page">
            {/* Header */}
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-3">
                    <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-rose-500/10 border border-rose-500/20">
                        <Eye className="h-6 w-6 text-rose-400" />
                    </div>
                    <div>
                        <h1 className="text-xl font-bold">تنبيهات المشاهدات الوهمية</h1>
                        <p className="text-sm text-muted-foreground">
                            سجلّ دائم لموجات المشاهدات المكتشفة عبر حساب المراقبة (MTProto)
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <Filter className="h-4 w-4 text-muted-foreground" />
                    <Select value={severity} onValueChange={setSeverity}>
                        <SelectTrigger className="w-[140px]" data-testid="views-severity-select">
                            <SelectValue placeholder="الخطورة" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">كل الخطورات</SelectItem>
                            <SelectItem value="critical">حرج فقط</SelectItem>
                            <SelectItem value="warning">تحذير فقط</SelectItem>
                        </SelectContent>
                    </Select>
                    <Button variant="outline" size="sm" onClick={load} className="gap-2" data-testid="views-refresh">
                        <RefreshCw className="h-4 w-4" /> تحديث
                    </Button>
                </div>
            </div>

            {/* KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Kpi label="إجمالي التنبيهات" value={stats.total} icon={Eye} color="cyan" testId="kpi-views-total" />
                <Kpi label="حرجة" value={stats.critical} icon={AlertCircle} color="rose" testId="kpi-views-critical" />
                <Kpi label="قنوات متأثّرة" value={stats.channels} icon={TrendingUp} color="violet" testId="kpi-views-channels" />
                <Kpi label="موجات متكرّرة" value={stats.repeats} icon={Repeat} color="amber" testId="kpi-views-repeats" />
            </div>

            {/* Table */}
            <Card className="bg-card/40 border-border/40">
                <CardHeader className="pb-3">
                    <CardTitle className="text-base">سجلّ الموجات</CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="overflow-x-auto">
                        <Table data-testid="views-alerts-table">
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="whitespace-nowrap">الوقت</TableHead>
                                    <TableHead className="whitespace-nowrap">القناة</TableHead>
                                    <TableHead className="text-center whitespace-nowrap">المنشور</TableHead>
                                    <TableHead className="text-center whitespace-nowrap">الزيادة</TableHead>
                                    <TableHead className="text-center whitespace-nowrap">من ← إلى</TableHead>
                                    <TableHead className="text-center whitespace-nowrap">النسبة</TableHead>
                                    <TableHead className="text-center whitespace-nowrap">الخطورة</TableHead>
                                    <TableHead className="text-center whitespace-nowrap">النوع</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {filtered.length === 0 && (
                                    <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-10">لا توجد تنبيهات مشاهدات بعد. 🎉</TableCell></TableRow>
                                )}
                                {filtered.map((e, i) => {
                                    const d = e.details || {};
                                    const g = groups[e.chat_id] || {};
                                    const sev = sevMeta[e.severity] || sevMeta.info;
                                    const msgId = d.message_id;
                                    return (
                                        <TableRow key={e.id || i} data-testid={`views-row-${i}`}>
                                            <TableCell className="whitespace-nowrap text-xs text-muted-foreground" dir="ltr">{fmtDate(e.ts)}</TableCell>
                                            <TableCell className="max-w-[200px]">
                                                <ChannelLink title={e.chat_title || g.title} username={g.username} chatId={e.chat_id} testId={`views-channel-${i}`} />
                                            </TableCell>
                                            <TableCell className="text-center">
                                                {msgId != null && g.username ? (
                                                    <a
                                                        href={`https://t.me/${g.username}/${msgId}`}
                                                        target="_blank" rel="noopener noreferrer"
                                                        className="inline-flex items-center gap-1 text-cyan-300 hover:underline text-xs font-mono"
                                                        dir="ltr"
                                                    >
                                                        #{msgId} <ExternalLink className="h-3 w-3" />
                                                    </a>
                                                ) : msgId != null ? (
                                                    <span className="text-xs font-mono text-muted-foreground" dir="ltr">#{msgId}</span>
                                                ) : <span className="text-muted-foreground">—</span>}
                                            </TableCell>
                                            <TableCell className="text-center font-bold text-rose-300" dir="ltr">+{fmt(d.delta)}</TableCell>
                                            <TableCell className="text-center text-muted-foreground text-xs" dir="ltr">{fmt(d.previous_views)} ← {fmt(d.current_views)}</TableCell>
                                            <TableCell className="text-center" dir="ltr">
                                                {d.ratio != null ? <span className="text-amber-300 font-medium">{d.ratio}×</span> : "—"}
                                            </TableCell>
                                            <TableCell className="text-center">
                                                <Badge variant="outline" className={`text-[11px] ${sev.cls}`}>{sev.label}</Badge>
                                            </TableCell>
                                            <TableCell className="text-center">
                                                {d.is_repeat
                                                    ? <Badge variant="outline" className="text-[11px] border-rose-500/30 text-rose-300"><Repeat className="h-3 w-3 mr-1" />تكرار #{d.spike_index}</Badge>
                                                    : <Badge variant="outline" className="text-[11px] border-border/40 text-muted-foreground">أولى #{d.spike_index || 1}</Badge>}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </div>
                    <p className="text-[11px] text-muted-foreground mt-3">
                        يُكتشف الهجوم عند نموٍّ نشِط في المشاهدات (≥300 خلال النافذة) ينتهي فوق المعدّل الطبيعي للقناة (≥2×). «أولى» = موجة أولى للمراقبة، «تكرار» = تأكيد للهجوم.
                    </p>
                </CardContent>
            </Card>
        </div>
    );
}
